import { useCallback, useEffect, useRef, useState } from "react";
import { ActiveSessionApi } from "../../../../api/revi/activeSessionApi";
import { useAuth } from "../../../../hooks";

const api = new ActiveSessionApi();

const POLL_MS = 15000;

export function usePatientPortalController() {
  const { accessToken } = useAuth();

  const [activeSession, setActiveSession] = useState(null);
  const [roomUrl, setRoomUrl] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const signatureRef = useRef("NONE");

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const applyStateFromResponse = useCallback((res) => {
    const s = res?.activeSession || null;
    const nextRoomUrl = s?.roomUrl || null;

    // Firma para detectar cambios (sesión / url / escena)
    const nextSignature = s
      ? `${s.sessionId}|${nextRoomUrl || ""}|${s.currentSceneIndex ?? 0}|${s.status || ""}|${s.updatedAt || ""}`
      : "NONE";

    if (nextSignature !== signatureRef.current) {
      signatureRef.current = nextSignature;
      setIframeKey((k) => k + 1); // fuerza reload iframe
    }

    setActiveSession(s);
    setRoomUrl(nextRoomUrl);
  }, []);

  const pollNow = useCallback(async () => {
    if (!accessToken) return;

    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      setError(null);

      const res = await api.getPatientActiveSession(accessToken);
      applyStateFromResponse(res);
    } catch (e) {
      // En paciente: ante error -> estado “esperando”
      setError(e?.message || "No se pudo comprobar la sesión.");
      signatureRef.current = "NONE";
      setActiveSession(null);
      setRoomUrl(null);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [accessToken, applyStateFromResponse]);

  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      await pollNow();
      clearTimer();
      timerRef.current = setTimeout(tick, POLL_MS);
    };

    setLoading(true);
    tick();

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [accessToken, pollNow]);

  return {
    loading,
    error,
    activeSession,
    roomUrl,
    iframeKey,
    actions: { pollNow },
  };
}
